// web/src/components/ds/DsChipPopover.tsx — DS.6 (sprint aos-ds-composer,
// spec §2 D4, BRANCH B — the default).
//
// WHY THIS FILE EXISTS (the measurement, not a preference): both
// `@ds/overlays/Menu` (:262-283) and `@ds/overlays/Popover` (:62-78) render
// `{...props}` on an OUTER wrapper and put the interaction on an INNER
// `<div role="button" tabIndex={0} onClick onKeyDown>` that receives NO
// forwarded props. A consumer therefore cannot put `data-testid`, a native
// `disabled` or a `tabIndex` on the node that actually activates. The two ways
// out through the pieces as shipped are both regressions:
//
//   * pass a real `<button>` (any `Chip` variant but `plain`) as `trigger` →
//     an interactive node nested inside a `role="button"` node with its own
//     Enter/Space handler ⇒ TWO state transitions per Enter, plus a
//     nested-interactive a11y defect;
//   * pass `variant="plain"` (a `<span>`, upstream's documented nesting
//     escape) → the testid moves off the interactive node AND a disabled chip
//     stays focusable and Enter-activatable. `ModeChip.test.tsx:50` asserts
//     `toBeDisabled()`, which jest-dom only honours on a real form control.
//
// So the ADAPTER owns the trigger and the DS piece is used for the SURFACE
// only: the caller renders a real `@ds/data-display/Chip variant="filter"`
// (Chip.tsx:111-121 — `<button disabled={disabled}>`) and spreads the props
// this component hands it. That is the `asChild`-style prop merging the
// upstream issue asks `Menu`/`Popover` for; until the pieces grow it, this is
// where it lives.
//
// The trigger contract this guarantees (spec D4, asserted by AC-21 in a
// running browser):
//   1. exactly ONE `button, [role="button"]` node per trigger subtree;
//   2. the `data-testid` is on THAT node;
//   3. `disabled` is NATIVE — `node.matches(':disabled')`;
//   4. exactly ONE open transition per Enter/Space: a real `<button>` already
//      fires `click` from Enter (keydown) and Space (keyup), so this component
//      deliberately adds NO Enter/Space handler of its own;
//   5. BOTH `aria-haspopup` and `aria-expanded` on that node — `Popover` ships
//      `aria-expanded` (:69) and no `aria-haspopup` at all, while `Menu` ships
//      both (:273-274), so the adapter owns both and the two chips expose the
//      same contract.
//
// `selected` is NEVER passed to the chip (spec R2 C5): `showCheck = variant
// === 'filter' && selected` (Chip.tsx:47) would emit a `material-symbols-
// rounded` `check` ligature at :70 inside the founder's chip, which no
// `IconFamilyProvider` covers. The chevron rides the `trailing` slot and the
// open state is `aria-expanded` alone.
//
// The panel is drawn from skin roles with DS.5's `DsMenu` keyboard model —
// Escape closes AND RETURNS FOCUS to the trigger, which the DS `Popover`'s
// document-level listener (:43-60) does not do, and a `focusout` that leaves
// the wrapper closes (tab-to-close). Bottom-anchored (`bottom: 100%`), because
// this is a composer.

import {
  useCallback,
  useEffect,
  useRef,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
  type Ref,
} from 'react';

/** What the caller spreads onto its `@ds/data-display/Chip`. */
export interface DsChipTriggerProps {
  ref: Ref<HTMLButtonElement>;
  type: 'button';
  'data-testid': string;
  'aria-haspopup': 'menu' | 'dialog';
  'aria-expanded': boolean;
  /** Always `undefined`, and that is a MEASURED correction, not a no-op.
   *  `Chip` renders `aria-pressed={variant === 'filter' ? selected : undefined}`
   *  (Chip.tsx:116) with `selected = false` as the DESTRUCTURED DEFAULT
   *  (:40), so a filter chip always emits `aria-pressed="false"` — the spec
   *  predicted `undefined`. A node that is both a toggle-button and a popup
   *  trigger announces two contradictory roles, so the adapter unsets it
   *  (the spread lands after Chip's own attribute, and `undefined` renders
   *  nothing). Upstream ask: emit `aria-pressed` only when `selected` was
   *  actually passed. */
  'aria-pressed': undefined;
  disabled: boolean | undefined;
  onClick: () => void;
  onKeyDown: (e: ReactKeyboardEvent<HTMLElement>) => void;
  /** Neutralises `index.css`'s zero-specificity 44 px touch floor on every
   *  `button`, which would otherwise stretch a 20 px DS chip to 44 px and
   *  break parity with `approved-composer.png`. Same shape the shadcn chips
   *  carried before this sprint (`style={{ minHeight: 'auto' }}`). */
  style: CSSProperties;
}

export interface DsChipPopoverProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  disabled?: boolean;
  /** Goes on the interactive trigger node — constraint 2. The prop is named
   *  for the attribute it becomes so the call site carries the LITERAL id: the
   *  campaign's testid census is a grep over these six sources, and an id that
   *  reaches the DOM only through a variable is invisible to it. */
  'data-testid': string;
  /** `menu` for a list of options, `dialog` for an arbitrary panel. */
  hasPopup?: 'menu' | 'dialog';
  renderTrigger: (props: DsChipTriggerProps) => ReactNode;
  children: ReactNode;
  /** Called with the panel node right after it opens (focus placement). */
  onOpenAutoFocus?: (panel: HTMLDivElement) => void;
  /** Extra inline style for the floating panel (width, padding…). */
  panelStyle?: CSSProperties;
}

const wrapperStyle: CSSProperties = {
  position: 'relative',
  display: 'inline-flex',
  alignItems: 'center',
};

const panelBaseStyle: CSSProperties = {
  position: 'absolute',
  bottom: '100%',
  left: 0,
  marginBottom: 'var(--space-2)',
  background: 'var(--bg-grey-lightest)',
  border: 'var(--border-width-default) solid var(--border-strong)',
  borderRadius: 'var(--radius-s)',
  boxShadow: 'var(--shadow-lg)',
  zIndex: 'var(--z-dropdown)' as unknown as number,
  minWidth: '100%',
  outline: 'none',
};

export function DsChipPopover({
  open,
  onOpenChange,
  disabled,
  'data-testid': triggerTestId,
  hasPopup = 'dialog',
  renderTrigger,
  children,
  onOpenAutoFocus,
  panelStyle,
}: DsChipPopoverProps) {
  const wrapperRef = useRef<HTMLSpanElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const autoFocusRef = useRef(onOpenAutoFocus);
  autoFocusRef.current = onOpenAutoFocus;

  const close = useCallback(
    (refocus: boolean) => {
      onOpenChange(false);
      if (refocus) triggerRef.current?.focus();
    },
    [onOpenChange],
  );

  // Outside mousedown closes (the DS pieces' own behaviour), and a focusout
  // that leaves the wrapper closes — DS.5's tab-to-close parity.
  useEffect(() => {
    if (!open) return;
    function onDocMouseDown(e: MouseEvent) {
      const el = wrapperRef.current;
      if (!el) return;
      if (e.target instanceof Node && el.contains(e.target)) return;
      onOpenChange(false);
    }
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [open, onOpenChange]);

  const panelRef = useCallback((node: HTMLDivElement | null) => {
    if (!node) return;
    autoFocusRef.current?.(node);
  }, []);

  function onTriggerKeyDown(e: ReactKeyboardEvent<HTMLElement>) {
    // NO Enter/Space branch: the trigger is a real <button>, which already
    // turns both into a click. Adding one here would open and close in the
    // same keypress (D4 constraint 4).
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      if (open) return;
      e.preventDefault();
      onOpenChange(true);
      return;
    }
    if (e.key === 'Escape' && open) {
      e.preventDefault();
      close(false);
    }
  }

  function onPanelKeyDown(e: ReactKeyboardEvent<HTMLDivElement>) {
    if (e.key !== 'Escape') return;
    e.preventDefault();
    e.stopPropagation();
    // The DS Popover's document listener closes but returns focus to nothing.
    close(true);
  }

  function onPanelFocusOut(e: React.FocusEvent<HTMLDivElement>) {
    const next = e.relatedTarget;
    const el = wrapperRef.current;
    if (!el) return;
    if (next instanceof Node && el.contains(next)) return;
    onOpenChange(false);
  }

  return (
    <span ref={wrapperRef} style={wrapperStyle}>
      {renderTrigger({
        ref: triggerRef,
        type: 'button',
        'data-testid': triggerTestId,
        'aria-haspopup': hasPopup,
        'aria-expanded': open,
        'aria-pressed': undefined,
        disabled,
        onClick: () => onOpenChange(!open),
        onKeyDown: onTriggerKeyDown,
        style: { minHeight: 0, minWidth: 0 },
      })}
      {open && (
        <div
          ref={panelRef}
          style={{ ...panelBaseStyle, ...panelStyle }}
          onKeyDown={onPanelKeyDown}
          onBlur={onPanelFocusOut}
        >
          {children}
        </div>
      )}
    </span>
  );
}

export default DsChipPopover;
